import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid-premium';
import React, { useState } from 'react';

import AttachmentUploader from '~/components/AttachmentUploader';
import type { Attachment } from '~/data/schemas/attachment';
import { BUTTON_SX } from '~/theme';

interface IUploadContractDialog {
  open: boolean;
  onClose: () => void;
  onFilesUploaded: (attachments: Attachment[]) => void;
  onNext: (attachments: Attachment[]) => void;
}

const UploadContractDialog: React.FC<IUploadContractDialog> = ({
  open,
  onClose,
  onFilesUploaded,
  onNext,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<Attachment[]>([]);

  const handleFilesUploaded = (attachments: Attachment[]) => {
    setUploadedFiles((prev) => [...prev, ...attachments]);
    onFilesUploaded(attachments);
  };

  const handleNext = () => {
    if (uploadedFiles.length > 0) {
      onNext(uploadedFiles);
    }
  };

  const handleClose = () => {
    setUploadedFiles([]);
    onClose();
  };

  const handleRemoveFile = (fileToRemove: Attachment) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileToRemove.id));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload Contract Documents
          <IconButton onClick={handleClose}>
            <GridCloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Upload contract documents
        </Typography>

        <AttachmentUploader
          fileSizeLimit={5}
          acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
          acceptedFileTypesLabel="PDF"
          onFilesUploaded={handleFilesUploaded}
        />

        {uploadedFiles.length > 0 ? (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Uploaded Documents
            </Typography>
            <List>
              {uploadedFiles.map((file) => (
                <ListItem key={file.id} disablePadding sx={{ mb: 1 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <DescriptionIcon color="primary" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2">{file.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        PDF Document
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => handleRemoveFile(file)}
                      size="small"
                      aria-label="remove file"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                </ListItem>
              ))}
            </List>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={uploadedFiles.length === 0}
                sx={BUTTON_SX}
              >
                Next
              </Button>
            </Stack>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default UploadContractDialog;
